import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';
import { User } from 'utils';

export interface AuthContext {
  isAuthenticated: boolean;
  login: (accessToken: string, user: User) => Promise<void>;
  logout: () => void;
  user: User | null;
}

const AuthContext = createContext<AuthContext | null>(null);

const USER_AUTH_TOKEN_KEY = 'USER_AUTH_TOKEN';
const USER_AUTH_KEY = 'USER_AUTH';

export function getStoredToken() {
  return localStorage.getItem(USER_AUTH_TOKEN_KEY);
}

export function setStoredToken(user: string | null) {
  if (user) {
    localStorage.setItem(USER_AUTH_TOKEN_KEY, user);
    return;
  }
  localStorage.removeItem(USER_AUTH_TOKEN_KEY);
}

export function setStoredUser(user: User | null) {
  if (user) {
    localStorage.setItem(USER_AUTH_KEY, JSON.stringify(user));
    return;
  }
  localStorage.removeItem(USER_AUTH_KEY);
}

function getStoredUser() {
  const user = localStorage.getItem(USER_AUTH_KEY);
  if (user) {
    return JSON.parse(user);
  }
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(getStoredUser());
  const isAuthenticated = !!user;

  const logout = useCallback(() => {
    setStoredToken(null);
    setUser(null);
    setStoredUser(null);
  }, []);

  const login = useCallback(async (accessToken: string, u: User) => {
    setStoredToken(accessToken);
    setStoredUser(u);
    setUser(u);
    // wait for state to be updated
    await new Promise(resolve => setTimeout(resolve));
  }, []);

  useEffect(() => {
    setUser(getStoredUser());
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
